import React, { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Col, Popover, Row } from 'antd';
import { Button } from '@mui/material';
import { SaveButton, SimpleForm, useRedirect } from 'react-admin';
import dayjs from 'dayjs';

import { CxCredit, CxHistory } from '@types';
import {
  Insurance,
  InvoiceAndCredit,
  MainInfo,
  Receipt,
  Tasks,
  Questions,
  ESGDocs,
  Comments,
  Tabs,
} from '../Components';
import { ConfirmDelete, Container, InitialPage } from '@components';
import { FieldReport } from '@pages/cx/Components/FieldReport';
import { FollowUp } from '@pages/cx/Components/FollowUp';
import cls from './CxWidget.module.css';
import { cxHttp } from '@network';

interface CxWidgetProps {
  cxCredit: CxCredit;
  editable: boolean;
}

const CxWidgetHistory = ({ history }: {history: CxHistory[]}) => {
  const [showAll, setShowAll] = useState(false);

  const displayedHistory = showAll ? history : history.slice(0, 3);
  const hasMoreItems = history.length > 3;

  const showMore = () => {
    setShowAll(prev => !prev);
  };

  const historyRender = () => (
    <div className={cls.history}>
      <div className={cls.historyTitle}>History</div>
      {displayedHistory.map(item => (
        <div key={`${item.id}-${item.title}`} className={cls.historyItem}>
          <div className={cls.historyHead}>
            {item.adminName && (
              <div className={cls.historyOwner}>by {item.adminName}</div>
            )}
            <div className={cls.historyDate}>{dayjs(item.createdAt).format('DD MMM YYYY, HH:mm')}</div>
          </div>
          <div className={cls.historyText}>{item.title}</div>
        </div>
      ))}

      {hasMoreItems && !showAll && (
        <div className={cls.showMoreBtn}>
          <Button
            size="small"
            variant="contained"
            onClick={showMore}
          >
            Show previous ({history.length - 3})
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={cls.historyWidget}>
      <div className={cls.historyWrap}>
        <Popover content={historyRender()}
          title={null}
          trigger="click"
          placement="bottomRight"
          className={cls.historyContent}>
          <Button variant="contained">View History</Button>
        </Popover>
      </div>
    </div>
  );
};

export const CxWidget = ({ cxCredit, editable }: CxWidgetProps) => {
  const title = `CX ${cxCredit.code}`;
  const redirect = useRedirect();
  const saveBtnRef = useRef<HTMLDivElement>(null);
  const [hasUpdated, setHasUpdated] = useState<boolean>(false);
  const [history, setHistory] = useState<CxHistory[]>([]);

  const handleCancel = () => {
    redirect('/cx/');
  };

  const saveForm = async () => {
    saveBtnRef.current?.querySelector('button')?.click();
  };

  useEffect(() => {
    if (cxCredit?.id) {
      (async () => {
        const nextHistory = await cxHttp.getHistory(cxCredit.id);
        setHistory(nextHistory);
      })();
    }
  }, [cxCredit]);

  return (
    <InitialPage title={title}
      backTo="/cx"
      backText="Back to all records"
      gap={20}
      headerRight={<CxWidgetHistory history={history} />}>
      <SimpleForm toolbar={false} defaultValues={cxCredit}>
        <Container max>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={24}>
              <MainInfo cxCredit={cxCredit} />
            </Col>

            <Col xs={24} lg={12} xl={6}>
              <InvoiceAndCredit cxCredit={cxCredit} />
            </Col>
            <Col xs={24} lg={12} xl={6}>
              <Tasks cxCredit={cxCredit} />
            </Col>
            <Col xs={24} lg={12} xl={6}>
              <Insurance cxCredit={cxCredit} />
            </Col>
            <Col xs={24} lg={12} xl={6}>
              <Receipt cxCredit={cxCredit} />
            </Col>

            <Col xs={24} lg={24} xl={24}>
              <FieldReport
                credit={cxCredit}
                hasUpdated={hasUpdated}
                setHasUpdated={setHasUpdated}
                compact
                editable={editable}
              />
            </Col>

            <Col xs={24} xl={12}>
              <Questions
                title="Satisfaction Survey"
                link={cxCredit.cx?.satisfactionSurveyLink || null}
                isSurveyCompleted={cxCredit.cx?.isSurveyCompleted || false}
                source="cx.questionsWelcomeCall"
              />
            </Col>
            <Col xs={24} xl={12}>
              <ESGDocs cxCredit={cxCredit} />
              <Comments />
            </Col>

            <Col xs={24} xl={24}>
              <FollowUp cxCredit={cxCredit} saveForm={saveForm} />
            </Col>

            <Col xs={24} sm={24}>
              <Tabs
                cxId={cxCredit.id}
                title="Covered Crop"
                source="cx.setsCoveredCrop"
                checkSource="cx.hasCoveredCrop"
                isCoveredCrop
                disabled={false}
              />
            </Col>

            <Col xs={24} sm={24}>
              <Tabs cxId={cxCredit.id} title="Crop Log" source="cx.setsCropLog" />
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            <Col xs={12} md={8} className={cls.actionsLeft}>
              <ConfirmDelete title="Are you sure?" cb={handleCancel}>
                <Button
                  variant="outlined"
                  color="inherit"
                  className={clsx(cls.action, cls.cancelBtn)}
                  onClick={undefined}
                >Go back</Button>
              </ConfirmDelete>
            </Col>
            <Col xs={12} md={16} className={cls.actionsRight}>
              <div ref={saveBtnRef}>
                <SaveButton label="Save" type="button" className={cls.action} />
              </div>
            </Col>
          </Row>
        </Container>
      </SimpleForm>
    </InitialPage>
  );
};
